import React from 'react'
import { StepDefinition, BackRule, CustomTransition } from '../types'

export type StepBuilder<C = any> = StepDefinition<C> & {
  withImage(url: string): StepBuilder<C>
  withExtra(fn: (ctx: C) => Record<string, any>): StepBuilder<C>
  withSiderComponent(component: React.FC<any>): StepBuilder<C>

  hideBackButton(): StepBuilder<C>
  allowSkip(targetWhenSkipped?: string): StepBuilder<C>
  isFullWidth(): StepBuilder<C>
  isFullScreen(): StepBuilder<C>

  skipWhen(fn: (ctx: C) => boolean): StepBuilder<C>
  back(target: string, cond?: (ctx: C) => boolean): StepBuilder<C>
  on(event: string, target?: string, cond?: (ctx: C) => boolean, customTransitions?: CustomTransition[]): StepBuilder<C>
  backgroundClassName(className: string): StepBuilder<C>
  withMobileLogo(): StepBuilder<C>
}

export function step<C = any>(id: string, component: React.FC<any>): StepBuilder<C> {
  const def: StepDefinition<C> = { id, component }

  const builder: StepBuilder<C> = Object.assign(def, {
    withImage(url: string) {
      def.image = url
      return builder
    },
    withExtra(fn: (ctx: C) => Record<string, any>) {
      def.extra = fn
      return builder
    },
    withSiderComponent(component: React.FC<any>) {
      def.siderComponent = component
      return builder
    },

    hideBackButton() {
      def.noBackButton = true
      return builder
    },
    allowSkip(targetWhenSkipped?: string) {
      def.skipAllowed = true
      if (targetWhenSkipped) (def as any).skipTarget = targetWhenSkipped
      return builder
    },
    isFullWidth() {
      def.fullWidth = true
      return builder
    },
    isFullScreen() {
      def.fullScreen = true
      return builder
    },

    skipWhen(fn: (ctx: C) => boolean) {
      def.shouldSkip = fn
      return builder
    },
    back(target: string, cond?: (ctx: C) => boolean) {
      ;(def.backRules ??= []).push({ target, cond } as BackRule<C>)
      return builder
    },
    on(event: string, target: string, cond?: (ctx: C) => boolean) {
      ;(def.customTransitions ??= {})[event] = {
        target,
        cond,
      } as CustomTransition<C>
      return builder
    },
    backgroundClassName(className: string) {
      def.backgroundClassName = className
      return builder
    },
    withMobileLogo() {
      def.showMobileLogo = true
      return builder
    },
  })

  return builder
}

export function defineSteps<C = any>(builders: StepBuilder<C>[]): StepDefinition<C>[] {
  const seen = new Set<string>()

  return builders.map((builder) => {
    if (seen.has(builder.id)) {
      throw new Error(`defineSteps: duplicate step id '${builder.id}' detected`)
    }
    seen.add(builder.id)

    const {
      id,
      component,
      image,
      siderComponent,
      extra,
      noBackButton,
      skipAllowed,
      skipTarget,
      shouldSkip,
      backRules,
      customTransitions,
      fullWidth,
      fullScreen,
      backgroundClassName,
      showMobileLogo,
    } = builder as StepDefinition<C>

    const def: StepDefinition<C> = {
      id,
      component,
      image,
      siderComponent,
      extra,
      noBackButton,
      skipAllowed,
      skipTarget,
      shouldSkip,
      backRules,
      customTransitions,
      fullWidth,
      fullScreen,
      backgroundClassName,
      showMobileLogo,
    }

    return def
  })
}
